/**
 * Optimized Session Manager - React 19 Enhanced
 * 
 * Simplified session management with reduced complexity and improved functionality.
 * Enhanced with React 19 concurrent features, performance monitoring, and event systems.
 * Now includes 7-day localStorage expiration for user sessions with development mode controls.
 */

import { isSampleDataMode } from '@/lib/config/env';
import { getAuth } from '@/lib/firebase/core';
import { UserSessionStorage, initializeEnhancedLocalStorage } from '@/lib/utils/localStorage';
import type { User } from '@/lib/types';
import { getDevelopmentSessionConfig } from '@/lib/config/development';
import { startTransition } from 'react';

// =============================================================================
// TYPES AND INTERFACES (React 19 Enhanced)
// =============================================================================

export interface SessionState {
  isAuthenticated: boolean;
  accessToken: string | null;
  user: User | null;
  tokenExpiry: number | null;
  lastRefresh: number | null;
  // React 19: Additional state tracking
  instanceId?: string;
  sessionStartTime?: number;
  requestCount?: number;
}

export interface AuthRequestConfig extends RequestInit {
  skipAuth?: boolean;
  retryCount?: number;
  timeout?: number;
  // React 19: Request priority and tracking (renamed to avoid conflict with RequestInit.priority)
  requestPriority?: 'high' | 'normal' | 'low';
  requestId?: string;
}

export interface SessionConfig {
  tokenRefreshThreshold: number; // Minutes before expiry to refresh
  maxRetries: number;
  requestTimeout: number; // Milliseconds
  autoTokenRefresh: boolean;
  // React 19: Enhanced configuration
  enablePerformanceTracking?: boolean;
  enableEventSystem?: boolean;
  maxConcurrentRequests?: number;
  // Enhanced localStorage configuration with development overrides
  enableLocalStoragePersistence?: boolean;
  localStorageExpirationDays?: number;
}

// React 19: Session event interface
export interface SessionEvent {
  type: 'auth_state_changed' | 'token_refreshed' | 'request_start' | 'request_complete' | 'request_error' | 'session_persisted' | 'session_restored';
  timestamp: number;
  data?: unknown;
  error?: string;
  requestId?: string;
}

// React 19: Performance metrics
export interface SessionMetrics {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  averageResponseTime: number;
  tokenRefreshCount: number;
  lastActivity: number;
}

// =============================================================================
// OPTIMIZED SESSION MANAGER (React 19 Enhanced)
// =============================================================================

export class SessionManager {
  private static readonly DEFAULT_CONFIG: SessionConfig = {
    tokenRefreshThreshold: 5, // Refresh 5 minutes before expiry
    maxRetries: 3,
    requestTimeout: 10000, // 10 seconds
    autoTokenRefresh: true,
    // React 19: Enhanced defaults
    enablePerformanceTracking: true,
    enableEventSystem: true,
    maxConcurrentRequests: 5,
    // Enhanced localStorage defaults with development overrides
    enableLocalStoragePersistence: true,
    localStorageExpirationDays: 7,
  };

  private sessionState: SessionState = {
    isAuthenticated: false,
    accessToken: null,
    user: null,
    tokenExpiry: null,
    lastRefresh: null,
  };

  private config: SessionConfig;
  private authStateUnsubscribe?: () => void;
  private tokenRefreshTimer?: NodeJS.Timeout;
  
  // React 19: Enhanced features
  private subscribers = new Set<(event: SessionEvent) => void>();
  private metrics: SessionMetrics;
  private activeRequests = new Map<string, Promise<Response>>();
  private instanceId: string;
  private developmentConfig = getDevelopmentSessionConfig();

  constructor(config: Partial<SessionConfig> = {}) {
    // Apply development configuration overrides
    const developmentOverrides = {
      enableLocalStoragePersistence: this.developmentConfig.enableLocalStoragePersistence,
      localStorageExpirationDays: this.developmentConfig.localStorageExpirationDays,
      enablePerformanceTracking: this.developmentConfig.enablePerformanceTracking,
      enableEventSystem: this.developmentConfig.enableEventSystem,
      tokenRefreshThreshold: this.developmentConfig.tokenRefreshThreshold,
    };

    this.config = { 
      ...SessionManager.DEFAULT_CONFIG, 
      ...developmentOverrides,
      ...config 
    };
    
    // React 19: Initialize instance tracking
    this.instanceId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // React 19: Initialize metrics
    this.metrics = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      averageResponseTime: 0,
      tokenRefreshCount: 0,
      lastActivity: 0,
    };
    
    // React 19: Update session state with instance info
    this.sessionState.instanceId = this.instanceId;
    this.sessionState.sessionStartTime = Date.now();
    this.sessionState.requestCount = 0;
    
    // Initialize enhanced localStorage with development configuration
    if (this.config.enableLocalStoragePersistence) {
      initializeEnhancedLocalStorage();
      
      if (this.developmentConfig.enablePerformanceTracking) {
        console.log('🔐 [DEV] SessionManager initialized with development configuration:', {
          localStorage: this.developmentConfig.enableLocalStoragePersistence ? '✅ Enabled' : '❌ Disabled',
          expiration: `${this.developmentConfig.localStorageExpirationDays} days`,
          tokenRefresh: `${this.developmentConfig.tokenRefreshThreshold} minutes`,
        });
      }
    }
    
    this.initializeAuthState();
  }

  // =============================================================================
  // REACT 19: EVENT SYSTEM
  // =============================================================================

  /**
   * React 19: Subscribe to session events
   */
  subscribe(callback: (event: SessionEvent) => void): () => void {
    if (!this.config.enableEventSystem) {
      return () => {}; // No-op if events disabled
    }

    this.subscribers.add(callback);
    
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * React 19: Emit session event
   */
  private emitEvent(event: SessionEvent): void {
    if (!this.config.enableEventSystem) return;

    this.subscribers.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.warn(`Session event callback error:`, error);
      }
    });
  }

  // =============================================================================
  // INITIALIZATION AND STATE MANAGEMENT (React 19 Enhanced)
  // =============================================================================

  /**
   * Initialize authentication state - always use Firebase Auth
   */
  private initializeAuthState(): void {
    // First, try to restore user session from localStorage immediately
    this.restoreUserSessionFromStorage();
    
    // Then initialize Firebase Auth
    this.initializeFirebaseAuth();
  }

  /**
   * Restore user session from localStorage for immediate availability on page reload
   */
  private restoreUserSessionFromStorage(): void {
    if (!this.config.enableLocalStoragePersistence || typeof window === 'undefined') {
      return;
    }

    try {
      const storedUser = UserSessionStorage.getUserSession<User>();
      const sessionInfo = UserSessionStorage.getUserSessionInfo();
      
      if (storedUser && sessionInfo.exists && !sessionInfo.expired) {
        // Restore user data immediately
        this.sessionState = {
          ...this.sessionState,
          user: storedUser,
          isAuthenticated: true, // Set as authenticated since we have valid user data
        };

        if (this.developmentConfig.enablePerformanceTracking) {
          console.log('🔐 [DEV] User session restored from localStorage');
        }

        this.emitEvent({
          type: 'session_restored',
          timestamp: Date.now(),
          data: { 
            hasUser: true,
            source: 'localStorage'
          },
        });
      } else if (sessionInfo.expired) {
        // Clear expired session
        this.clearPersistedSession();
        
        if (this.developmentConfig.enablePerformanceTracking) {
          console.log('🔐 [DEV] Expired user session cleared from localStorage');
        }
      }
    } catch (error) {
      console.warn('Failed to restore user session from localStorage:', error);
      // Clear potentially corrupted session data
      this.clearPersistedSession();
    }
  }

  /**
   * Initialize Firebase authentication state
   */
  private initializeFirebaseAuth(): void {
    // Only initialize Firebase Auth in browser environment
    if (typeof window === 'undefined') {
      // Server-side environment - Firebase Auth not available
      return;
    }
    
    try {
      const auth = getAuth();
      
      if (!auth) {
        console.warn('Firebase Auth not available');
        return;
      }
      
      this.authStateUnsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
        if (firebaseUser) {
          await this.handleAuthenticatedUser(firebaseUser);
        } else {
          this.clearSession();
        }
      });
    } catch (error) {
      console.error('Failed to initialize Firebase auth:', error);
      this.emitEvent({
        type: 'request_error',
        timestamp: Date.now(),
        error: 'Firebase auth initialization failed',
      });
    }
  }

  /**
   * Handle authenticated Firebase user with development-aware session persistence
   */
  private async handleAuthenticatedUser(firebaseUser: { getIdToken: () => Promise<string>; getIdTokenResult: () => Promise<{ expirationTime: string }> }): Promise<void> {
    try {
      const [token, tokenResult] = await Promise.all([
        firebaseUser.getIdToken(),
        firebaseUser.getIdTokenResult(),
      ]);

      const expirationTime = new Date(tokenResult.expirationTime).getTime();

      // Try to restore user data from localStorage if not already present
      let userData = this.sessionState.user;
      if (!userData && this.config.enableLocalStoragePersistence) {
        const storedUser = UserSessionStorage.getUserSession<User>();
        const sessionInfo = UserSessionStorage.getUserSessionInfo();
        
        if (storedUser && sessionInfo.exists && !sessionInfo.expired) {
          userData = storedUser;
          
          if (this.developmentConfig.enablePerformanceTracking) {
            console.log('🔐 [DEV] User data restored from localStorage during Firebase auth');
          }
        }
      }

      this.sessionState = {
        ...this.sessionState,
        isAuthenticated: true,
        accessToken: token,
        tokenExpiry: expirationTime,
        lastRefresh: Date.now(),
        user: userData, // Include user data if available
      };

      this.scheduleTokenRefresh();
      this.persistSession();

      this.emitEvent({
        type: 'auth_state_changed',
        timestamp: Date.now(),
        data: { authenticated: true, hasUserData: !!userData },
      });
    } catch (error) {
      console.error('Error handling authenticated user:', error);
      this.emitEvent({
        type: 'request_error',
        timestamp: Date.now(),
        error: 'Failed to handle authenticated user',
      });
    }
  }

  /**
   * Schedule token refresh with development-aware timing
   */
  private scheduleTokenRefresh(): void {
    if (!this.config.autoTokenRefresh || !this.sessionState.tokenExpiry) return;

    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
    }

    const refreshTime = this.sessionState.tokenExpiry - (this.config.tokenRefreshThreshold * 60 * 1000);
    const delay = Math.max(0, refreshTime - Date.now());

    if (this.developmentConfig.enablePerformanceTracking) {
      console.log(`🔐 [DEV] Token refresh scheduled in ${Math.round(delay / 1000)}s`);
    }

    this.tokenRefreshTimer = setTimeout(() => {
      this.refreshToken();
    }, delay);
  }

  /**
   * Refresh authentication token - always use Firebase Auth
   */
  private async refreshToken(): Promise<string | null> {
    // Only attempt token refresh in browser environment
    if (typeof window === 'undefined') {
      return null;
    }
    
    try {
      const auth = getAuth();
      
      if (!auth) {
        throw new Error('Firebase Auth not available');
      }
      
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      const token = await currentUser.getIdToken(true); // Force refresh
      const tokenResult = await currentUser.getIdTokenResult();
      const expirationTime = new Date(tokenResult.expirationTime).getTime();

      this.sessionState = {
        ...this.sessionState,
        accessToken: token,
        tokenExpiry: expirationTime,
        lastRefresh: Date.now(),
      };

      this.metrics.tokenRefreshCount++;
      this.scheduleTokenRefresh();

      this.emitEvent({
        type: 'token_refreshed',
        timestamp: Date.now(),
        data: { newExpiry: expirationTime },
      });

      if (this.developmentConfig.enablePerformanceTracking) {
        console.log('🔐 [DEV] Token refreshed successfully');
      }

      return token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.emitEvent({
        type: 'request_error',
        timestamp: Date.now(),
        error: 'Token refresh failed',
      });
      return null;
    }
  }

  // =============================================================================
  // AUTHENTICATED REQUESTS (React 19 Enhanced)
  // =============================================================================

  /**
   * Make authenticated HTTP request with React 19 enhancements
   */
  async authenticatedFetch(
    input: RequestInfo | URL,
    config: AuthRequestConfig = {}
  ): Promise<Response> {
    const {
      skipAuth = false,
      timeout = this.config.requestTimeout,
      requestPriority = 'normal',
      requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...init
    } = config;

    // React 19: Track request start
    this.metrics.totalRequests++;
    this.metrics.lastActivity = Date.now();
    this.sessionState.requestCount = (this.sessionState.requestCount || 0) + 1;

    const startTime = Date.now();

    this.emitEvent({
      type: 'request_start',
      timestamp: startTime,
      requestId,
      data: { url: input.toString(), priority: requestPriority },
    });

    try {
      // Check for existing request (React 19: Request deduplication)
      const requestKey = `${input.toString()}_${JSON.stringify(init)}`;
      if (this.activeRequests.has(requestKey)) {
        const existingRequest = this.activeRequests.get(requestKey)!;
        return await existingRequest;
      }

      // Ensure valid token if auth required
      if (!skipAuth) {
        await this.ensureValidToken();
      }

      // Prepare headers
      const headers = new Headers(init.headers);
      if (!skipAuth && this.sessionState.accessToken) {
        Object.entries(this.getAuthHeaders(this.sessionState.accessToken)).forEach(([key, value]) => {
          headers.set(key, value);
        });
      }

      // Make request with timeout
      const requestPromise = this.makeRequest(input, { ...init, headers }, timeout);
      this.activeRequests.set(requestKey, requestPromise);

      const response = await requestPromise;

      // Clean up active request
      this.activeRequests.delete(requestKey);

      // React 19: Update metrics
      const responseTime = Date.now() - startTime;
      this.metrics.successfulRequests++;
      this.metrics.averageResponseTime = 
        (this.metrics.averageResponseTime * (this.metrics.successfulRequests - 1) + responseTime) / 
        this.metrics.successfulRequests;

      this.emitEvent({
        type: 'request_complete',
        timestamp: Date.now(),
        requestId,
        data: { 
          status: response.status, 
          responseTime,
          priority: requestPriority 
        },
      });

      return response;
    } catch (error) {
      // Clean up active request
      const requestKey = `${input.toString()}_${JSON.stringify(init)}`;
      this.activeRequests.delete(requestKey);

      this.metrics.failedRequests++;

      this.emitEvent({
        type: 'request_error',
        timestamp: Date.now(),
        requestId,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      throw error;
    }
  }

  /**
   * Make HTTP request with timeout
   */
  private async makeRequest(
    input: RequestInfo | URL,
    init: RequestInit,
    timeout: number
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(input, {
        ...init,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Check if token needs refresh
   */
  private needsTokenRefresh(): boolean {
    if (!this.sessionState.tokenExpiry) return false;
    
    const refreshThreshold = this.config.tokenRefreshThreshold * 60 * 1000;
    return (this.sessionState.tokenExpiry - Date.now()) <= refreshThreshold;
  }

  /**
   * Ensure valid authentication token
   */
  private async ensureValidToken(): Promise<string> {
    if (!this.sessionState.accessToken) {
      throw new Error('No access token available');
    }

    if (this.needsTokenRefresh()) {
      const newToken = await this.refreshToken();
      if (!newToken) {
        throw new Error('Failed to refresh token');
      }
      return newToken;
    }

    return this.sessionState.accessToken;
  }

  /**
   * Get authentication headers
   */
  private getAuthHeaders(token: string): Record<string, string> {
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  // =============================================================================
  // SESSION PERSISTENCE (Enhanced with Development Configuration)
  // =============================================================================

  /**
   * Persist session to localStorage with development-aware expiration
   */
  private persistSession(): void {
    if (!this.config.enableLocalStoragePersistence) return;

    try {
      // Always persist Firebase session data
      if (this.sessionState.user) {
        UserSessionStorage.setUserSession(this.sessionState.user);
      }

      if (this.developmentConfig.enablePerformanceTracking) {
        console.log('🔐 [DEV] Session persisted to localStorage');
      }

      this.emitEvent({
        type: 'session_persisted',
        timestamp: Date.now(),
        data: { 
          hasUser: !!this.sessionState.user,
          storage: 'localStorage'
        },
      });
    } catch (error) {
      console.warn('Failed to persist session:', error);
    }
  }

  /**
   * Clear persisted session with development-aware cleanup
   */
  private clearPersistedSession(): void {
    if (!this.config.enableLocalStoragePersistence) return;

    try {
      UserSessionStorage.removeUserSession();
      
      if (this.developmentConfig.enablePerformanceTracking) {
        console.log('🔐 [DEV] Persisted session cleared from localStorage');
      }
    } catch (error) {
      console.warn('Failed to clear persisted session:', error);
    }
  }

  // =============================================================================
  // PUBLIC API (React 19 Enhanced)
  // =============================================================================

  /**
   * Get current session state (readonly)
   */
  getSessionState(): Readonly<SessionState> {
    return { ...this.sessionState };
  }

  /**
   * Update session state (limited to user data)
   */
  updateSessionState(updates: Partial<Pick<SessionState, 'user'>>): void {
    startTransition(() => {
      // If user is being set, automatically set isAuthenticated to true
      // If user is being cleared (null), set isAuthenticated to false
      const authenticationUpdate = updates.user !== undefined ? {
        isAuthenticated: updates.user !== null
      } : {};
      
      this.sessionState = {
        ...this.sessionState,
        ...updates,
        ...authenticationUpdate,
      };
      
      // Persist updated session
      this.persistSession();
      
      this.emitEvent({
        type: 'auth_state_changed',
        timestamp: Date.now(),
        data: { ...updates, ...authenticationUpdate },
      });
    });
  }

  /**
   * Check if session is valid
   */
  isSessionValid(): boolean {
    if (!this.sessionState.isAuthenticated || !this.sessionState.user) {
      return false;
    }

    // For sample mode, check localStorage expiration if enabled
    if (isSampleDataMode() && this.config.enableLocalStoragePersistence) {
      const sessionInfo = UserSessionStorage.getUserSessionInfo();
      return sessionInfo.exists && !sessionInfo.expired;
    }

    // For Firebase mode, check token expiry
    return !this.isTokenExpired();
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    if (!this.sessionState.tokenExpiry) return true;
    return Date.now() >= this.sessionState.tokenExpiry;
  }

  /**
   * Get time remaining until token expires (in milliseconds)
   */
  getTokenTimeRemaining(): number {
    if (!this.sessionState.tokenExpiry) return 0;
    return Math.max(0, this.sessionState.tokenExpiry - Date.now());
  }

  /**
   * Force token refresh
   */
  async forceTokenRefresh(): Promise<void> {
    await this.refreshToken();
  }

  /**
   * Clear session with development-aware cleanup
   */
  clearSession(): void {
    // Clear timers
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = undefined;
    }

    // Clear persisted session
    this.clearPersistedSession();

    // Reset session state
    this.sessionState = {
      isAuthenticated: false,
      accessToken: null,
      user: null,
      tokenExpiry: null,
      lastRefresh: null,
      instanceId: this.instanceId,
      sessionStartTime: this.sessionState.sessionStartTime,
      requestCount: this.sessionState.requestCount,
    };

    if (this.developmentConfig.enablePerformanceTracking) {
      console.log('🔐 [DEV] Session cleared');
    }

    this.emitEvent({
      type: 'auth_state_changed',
      timestamp: Date.now(),
      data: { authenticated: false },
    });
  }

  /**
   * Get session statistics
   */
  getSessionStats(): {
    isValid: boolean;
    mode: 'firebase';
    tokenTimeRemaining: number;
    lastRefresh: Date | null;
    autoRefreshEnabled: boolean;
    developmentConfig?: any;
  } {
    return {
      isValid: this.isSessionValid(),
      mode: 'firebase', // Always Firebase mode
      tokenTimeRemaining: this.getTokenTimeRemaining(),
      lastRefresh: this.sessionState.lastRefresh ? new Date(this.sessionState.lastRefresh) : null,
      autoRefreshEnabled: this.config.autoTokenRefresh,
      developmentConfig: this.developmentConfig.enablePerformanceTracking ? this.developmentConfig : undefined,
    };
  }

  /**
   * Dispose of session manager
   */
  dispose(): void {
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
    }

    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
    }

    this.subscribers.clear();
    this.activeRequests.clear();

    if (this.developmentConfig.enablePerformanceTracking) {
      console.log('🔐 [DEV] SessionManager disposed');
    }
  }
}

// =============================================================================
// SINGLETON INSTANCE AND UTILITIES
// =============================================================================

export const sessionManager = new SessionManager();

/**
 * Development utilities for testing session expiration
 */
export const sessionDevelopmentUtils = {
  /**
   * Force expire current session for testing
   */
  forceExpireSession(): void {
    if (process.env.NODE_ENV !== 'development') return;

    const config = getDevelopmentSessionConfig();
    if (config.enableLocalStoragePersistence) {
      // Force expire localStorage session
      try {
        const item = localStorage.getItem('courtlyUser');
        if (item) {
          const parsed = JSON.parse(item);
          if (parsed.expiresAt) {
            parsed.expiresAt = Date.now() - 1000; // Set to past
            localStorage.setItem('courtlyUser', JSON.stringify(parsed));
            console.log('⏰ [DEV] Session force-expired for testing');
          }
        }
      } catch (error) {
        console.warn('Failed to force expire session:', error);
      }
    } else {
      // Clear sessionStorage session
      sessionStorage.removeItem('courtlyUser');
      console.log('⏰ [DEV] SessionStorage session cleared for testing');
    }
  },

  /**
   * Get detailed session info for debugging
   */
  getDetailedSessionInfo(): {
    session: any;
    storage: any;
    config: any;
  } {
    const session = sessionManager.getSessionStats();
    const config = getDevelopmentSessionConfig();
    
    let storage: any = null;
    if (config.enableLocalStoragePersistence) {
      try {
        const item = localStorage.getItem('courtlyUser');
        storage = item ? JSON.parse(item) : null;
      } catch {
        storage = 'invalid';
      }
    } else {
      try {
        const item = sessionStorage.getItem('courtlyUser');
        storage = item ? JSON.parse(item) : null;
      } catch {
        storage = 'invalid';
      }
    }

    return { session, storage, config };
  },

  /**
   * Clear all session data for testing
   */
  clearAllSessionData(): void {
    if (process.env.NODE_ENV !== 'development') return;

    try {
      localStorage.removeItem('courtlyUser');
      sessionStorage.removeItem('courtlyUser');
      console.log('🧹 [DEV] All session data cleared for testing');
    } catch (error) {
      console.warn('Failed to clear session data:', error);
    }
  },
}; 
